<template>
  <v-dialog v-model="show" persistent transition="dialog-bottom-transition">
    <v-form ref="form" v-model="valid" :lazy-validation="lazy">
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Editar Consumo</v-toolbar-title>
        </v-toolbar>

        <v-card-text>
          <!-- Toggle modo producto -->
          <v-row class="mt-3 mb-1">
            <v-col cols="12">
              <v-switch
                v-model="modoProducto"
                label="Cargar producto del catálogo"
                inset
                dense
                hide-details
                color="primary"
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Bloque modo producto -->
          <v-row v-show="modoProducto" class="mt-2">
            <v-col cols="12">
              <v-alert v-if="errorCargaProductos" type="error" dense>
                No se pudo cargar el catálogo de productos. Intente nuevamente.
              </v-alert>
              <v-autocomplete
                v-model="productoSeleccionado"
                :items="catalogoProductos"
                :loading="cargandoProductos"
                item-text="nombre"
                item-value="idProducto"
                return-object
                prepend-icon="mdi-package-variant"
                label="Producto *"
                dense
                clearable
                no-data-text="No hay productos activos disponibles"
                :filter="
                  (item, queryText) =>
                    item.nombre
                      .toLowerCase()
                      .includes(queryText.toLowerCase()) ||
                    (item.codigo &&
                      item.codigo
                        .toLowerCase()
                        .includes(queryText.toLowerCase()))
                "
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.nombre }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.codigo"
                      >Código: {{ item.codigo }}</v-list-item-subtitle
                    >
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <v-text-field
                v-model.number="cantidad"
                prepend-icon="mdi-counter"
                label="Cantidad *"
                type="number"
                min="1"
                dense
                @keypress="onlyNumber"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <v-text-field
                v-model="precioUnitario"
                prepend-icon="mdi-currency-usd"
                label="Precio unitario *"
                dense
                @keypress="onlyNumber"
                @blur="toFixedPrecio"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="3">
              <v-text-field
                :value="totalCalculado"
                prepend-icon="mdi-calculator"
                label="Total"
                dense
                disabled
                prefix="$"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Fecha (siempre visible) -->
          <v-row class="mt-2">
            <v-col cols="12" sm="6" md="6">
              <v-text-field
                v-model="fechaConsumo"
                :value="fechaConsumo"
                label="Fecha Consumo *"
                :rules="requiredRules"
                prepend-icon="mdi-calendar"
                type="date"
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Descripción e importe (solo modo manual) -->
          <v-row v-show="!modoProducto">
            <v-col cols="12">
              <v-text-field
                v-model="descripcion"
                prepend-icon="mdi-account"
                label="Descripcion"
                dense
                maxlength="200"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row v-show="!modoProducto">
            <v-col cols="12" sm="6" md="6">
              <v-text-field
                v-model="total"
                prepend-icon="mdi-currency-usd"
                :rules="modoProducto ? [] : totalRules"
                label="Importe *"
                @blur="toFixed"
                @keypress="onlyNumber"
                dense
                maxlength="10"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey lighten-1" @click.stop="show = false"
            >Cancelar</v-btn
          >
          <v-btn
            color="success"
            :disabled="
              !valid ||
              (modoProducto && (!productoSeleccionado || cantidad <= 0))
            "
            @click.stop="modificarConsumo"
            >Guardar</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import axios from "@/axios-auth";
import { showAlert } from "@/utils/toast";
import moment from "moment";

export default {
  data: () => ({
    valid: false,
    lazy: false,
    fechaConsumo: null,
    descripcion: "",
    total: null,
    idTurno: null,
    idInscripcion: null,
    // Modo producto
    modoProducto: false,
    productoSeleccionado: null,
    catalogoProductos: [],
    cargandoProductos: false,
    errorCargaProductos: false,
    cantidad: 1,
    precioUnitario: null,
    requiredRules: [(v) => !!v || "Dato obligatorio"],
    totalRules: [
      (v) => !!v || "Dato obligatorio",
      (v) => v > 0 || "El total debe ser mayor a 0",
    ],
  }),
  props: {
    visible: { required: false },
    idConsumo: { required: false },
  },
  computed: {
    show: {
      get() {
        if (this.visible) this.buscarDatosConsumo();
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$refs.form.resetValidation();
          this.vaciarModels();
          this.$emit("close", false);
        }
      },
    },
    totalCalculado() {
      if (
        !this.cantidad ||
        this.precioUnitario === null ||
        this.precioUnitario === undefined
      )
        return 0;
      const c = parseFloat(this.cantidad);
      const p = parseFloat(this.precioUnitario);
      if (isNaN(c) || isNaN(p)) return 0;
      return parseFloat((c * p).toFixed(2));
    },
  },
  watch: {
    modoProducto(val) {
      if (val) {
        this.descripcion = "";
        this.total = null;
        if (!this.catalogoProductos.length) {
          this.cargarProductos();
        }
      } else {
        this.productoSeleccionado = null;
        this.cantidad = 1;
        this.precioUnitario = null;
      }
    },
    productoSeleccionado(producto) {
      if (producto) {
        this.precioUnitario = producto.precioVenta;
      }
    },
  },
  methods: {
    modificarConsumo() {
      if (moment(this.fechaConsumo).isBefore(moment("1900-01-01"))) {
        showAlert({
          icon: "warning",
          title: "La fecha del consumo debe ser mayor o igual al 01/01/1900.",
          timer: 2500,
          vm: this,
        });
        return;
      }
      this.$swal({
        title: "Modificar consumo",
        text: "¿Desea modificar el consumo?",
        icon: "success",
        type: "success",
        showCancelButton: true,
        background: "#ececec",
        confirmButtonText: "Si",
        cancelButtonText: "No",
        showCloseButton: true,
        showLoaderOnConfirm: true,
      }).then((result) => {
        if (result.value) {
          const datosConsumo = {
            fecha: this.fechaConsumo,
            idTurno: this.idTurno,
            idInscripcion: this.idInscripcion,
            total: this.modoProducto ? this.totalCalculado : this.total,
            descripcion: this.modoProducto
              ? this.productoSeleccionado.nombre
              : this.descripcion,
            idProducto: this.modoProducto
              ? this.productoSeleccionado.idProducto
              : null,
            cantidad: this.modoProducto ? this.cantidad : null,
            usuario: this.$store.state.usuario.usuario,
            idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
            idConsumo: this.idConsumo,
          };
          let self = this;
          axios
            .post("/Caja/ModificarConsumo", datosConsumo)
            .then(() => {
              self.$refs.form.resetValidation();
              self.vaciarModels();
              self.showAlert({
                icon: "success",
                title: "Consumo modificado.",
                vm: self,
                timer: 2000,
              });
              self.$emit("close", true);
            })
            .catch(() => {
              self.showAlert({
                icon: "warning",
                title: "El consumo no pudo ser modificado.",
                vm: this,
                timer: 2000,
              });
            });
        }
      });
    },

    buscarDatosConsumo() {
      let self = this;
      axios
        .get("/Caja/BuscarDatosConsumo", {
          params: { idConsumo: this.idConsumo },
        })
        .then((response) => {
          self.fechaConsumo = response.data.fecha.substring(0, 10);
          self.descripcion = response.data.descripcion;
          self.total = parseFloat(response.data.total).toFixed(2);
          self.idTurno = response.data.idTurno;
          self.idInscripcion = response.data.idInscripcion;

          // Si el consumo tiene producto, activar modoProducto y pre-cargar
          if (response.data.idProducto) {
            self.cantidad = response.data.cantidad || 1;
            // Cargar catálogo y luego seleccionar el producto
            self.cargarProductos().then(() => {
              const prod = self.catalogoProductos.find(
                (p) => p.idProducto === response.data.idProducto,
              );
              if (prod) {
                self.productoSeleccionado = prod;
                self.precioUnitario =
                  self.cantidad > 0
                    ? parseFloat(
                        (response.data.total / self.cantidad).toFixed(2),
                      )
                    : prod.precioVenta;
              }
              self.modoProducto = true;
            });
          }
        })
        .catch(() => {});
    },

    async cargarProductos() {
      this.cargandoProductos = true;
      this.errorCargaProductos = false;
      try {
        const { data } = await axios.get("/Producto/GetProductos", {
          params: {
            idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
            estado: "Activo",
            nombre: "%",
            codigo: "%",
            idCategoria: 0,
          },
        });
        this.catalogoProductos = data || [];
      } catch {
        this.errorCargaProductos = true;
        this.catalogoProductos = [];
      } finally {
        this.cargandoProductos = false;
      }
    },

    toFixed() {
      if (isNaN(parseFloat(this.total)))
        this.total = parseFloat(0.0).toFixed(2);
      else this.total = parseFloat(this.total).toFixed(2);
    },
    toFixedPrecio() {
      if (isNaN(parseFloat(this.precioUnitario)))
        this.precioUnitario = parseFloat(0.0).toFixed(2);
      else this.precioUnitario = parseFloat(this.precioUnitario).toFixed(2);
    },
    onlyNumber($event) {
      let keyCode = $event.keyCode ? $event.keyCode : $event.which;
      if ((keyCode < 48 || keyCode > 57) && keyCode != 46) {
        $event.preventDefault();
      }
    },
    vaciarModels() {
      this.descripcion = "";
      this.total = "";
      this.fechaConsumo = null;
      this.idTurno = null;
      this.idInscripcion = null;
      this.modoProducto = false;
      this.productoSeleccionado = null;
      this.cantidad = 1;
      this.precioUnitario = null;
      this.errorCargaProductos = false;
    },
    showAlert(alertInfo) {
      showAlert(alertInfo);
    },
  },
};
</script>
